import React, {
  CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { styled, keyframes, css } from 'styled-components';
import Draggable from 'react-draggable';
import { DrawingCanvas, DrawingMode } from './DrawingCanvas';
import { Placement, Position, Snapshot } from './types';
import ftcLogo from './assets/ftc_cimer.png';
import cnabLogo from './assets/cnab_logo.png';
import kesiLogo from './assets/kesi_logo.png';
import psnLogo from './assets/psn_logo.png';
import { ReactComponent as PlayerOpponentDisk } from './assets/player-opponent.svg';
import { ReactComponent as PlayerOwnDisk } from './assets/player-us.svg';
import { ReactComponent as BallDisk } from './assets/ball.svg';
import { ReactComponent as GoalKeeperDisk } from './assets/player-goalkeeper.svg';
import { green, red, white, yellow } from './components/colors';
import { useStore } from '@nanostores/react';
import { $auth } from './store/auth';
import { MovementMode } from './store/app';
import { PathOverlay, LiveTrail } from './components/plays/PathOverlay';
import {
  MIN_SAMPLE_DISTANCE,
  PieceKind,
  applyDragToPathSet,
} from './utils/path';

const LINE_WIDTH = 0.15;
const GOAL_LINE_X = 1;
const LINE_2M_X = GOAL_LINE_X + 2;
const LINE_5M_X = GOAL_LINE_X + 6;
const YELLOW_AREA_WIDTH = 4;
const GREEN_AREA_WIDTH = 5;
const GOAL_WIDTH = 3;
const GOAL_DEPTH = 1;
const POOL_PADDING_X = 0.05;
const POOL_PADDING_Y = 0.05;
const POOL_WIDTH = 25;
const POOL_HEIGHT = 15; // should be 20
// const POOL_FULL_WIDTH = POOL_WIDTH + GOAL_LINE_X + 2 * POOL_PADDING_X
const POOL_FULL_WIDTH = POOL_WIDTH + 2 * POOL_PADDING_X;
const POOL_FULL_HEIGHT = POOL_HEIGHT + 2 * POOL_PADDING_Y;
const GOAL_AREA_HEIGHT = 2 * 2 + GOAL_WIDTH;
const GOAL_AREA_WIDTH = 2;

const PLAYER_SIZE = 1.2;

const Pool = styled.div<{
  $placement: Placement;
  $ratio: number;
  $halfView: boolean;
  $isDrawingEnabled: boolean;
}>`
  position: relative;
  box-sizing: content-box;
  height: ${({ $placement }) => `${$placement.height}px`};
  width: ${({ $placement }) => `${$placement.width}px`};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const PositionedDrawingCanvas = styled(DrawingCanvas)<{
  $placement: Placement;
  $ratio: number;
  $isActive: boolean;
}>`
  position: absolute;
  box-sizing: content-box;
  top: ${(props) => `${(props.$placement.y as number) - props.$ratio * 0.1}px`};
  left: ${(props) =>
    `${(props.$placement.x as number) - props.$ratio * 0.1}px`};
  z-index: ${(props) => (props.$isActive ? 20 : 0)};
`;

const PlayArea = styled.div<{
  $placement: Placement;
  $ratio: number;
  $halfView: boolean;
  $isDrawingEnabled: boolean;
}>`
  position: absolute;
  box-sizing: border-box;
  border-color: ${green};
  border-width: ${({ $ratio }) => `${$ratio * 0.15}px`};
  border-style: solid;
  border-radius: ${({ $ratio }) => $ratio * (24 / 80)}px;
  top: ${({ $placement }) => `${$placement.y as number}px`};
  left: ${({ $placement }) => `${$placement.x as number}px`};
  height: ${({ $placement }) => `${$placement.height}px`};
  width: ${({ $placement }) => `${$placement.width}px`};
  z-index: 10;
  ${({ $halfView }) =>
    $halfView
      ? 'border-top: none;'
      : `
        border-left: none;
        border-right: none;
        // left: 0;
      `}
`;

const RedAreaLeftSide = styled.div<{
  $placement: Placement;
  $ratio: number;
  $halfView: boolean;
  $isDrawingEnabled: boolean;
  $reverse: boolean;
}>`
  position: absolute;
  box-sizing: border-box;
  ${({ $halfView, $reverse, $ratio, $placement }) => {
    return $halfView
      ? `
      border-top: solid ${$ratio * 0.15}px ${white};
      border-left: solid ${$ratio * 0.15}px ${red};
      border-top-left-radius: ${$ratio * (24 / 80)}px;
      top: ${$placement.y}px;
      left: ${$placement.x}px;
    `
      : $reverse
        ? `
      border-left: solid ${$ratio * 0.15}px ${white};
      border-bottom: solid ${$ratio * 0.15}px ${red};
      border-bottom-left-radius: ${$ratio * (24 / 80)}px;
      bottom: ${$placement.y}px;
      left: ${$placement.x}px;
      `
        : `
      border-left: solid ${$ratio * 0.15}px ${white};
      border-top: solid ${$ratio * 0.15}px ${red};
      border-top-left-radius: ${$ratio * (24 / 80)}px;
      top: ${$placement.y}px;
      left: ${$placement.x}px;
    `;
  }}
  height: ${({ $placement }) => `${$placement.height}px`};
  width: ${({ $placement }) => `${$placement.width}px`};
  z-index: 15;
`;

const RedAreaRightSide = styled.div<{
  $placement: Placement;
  $ratio: number;
  $halfView: boolean;
  $isDrawingEnabled: boolean;
  $reverse: boolean;
}>`
  position: absolute;
  box-sizing: border-box;
  ${({ $halfView, $reverse, $ratio, $placement }) => {
    return $halfView
      ? `
      border-top: solid ${$ratio * 0.15}px ${white};
      border-right: solid ${$ratio * 0.15}px ${red};
      border-top-right-radius: ${$ratio * (24 / 80)}px;
      top: ${$placement.y}px;
      right: ${$placement.x}px;
    `
      : $reverse
        ? `
      border-right: solid ${$ratio * 0.15}px ${white};
      border-bottom: solid ${$ratio * 0.15}px ${red};
      border-bottom-right-radius: ${$ratio * (24 / 80)}px;
      bottom: ${$placement.y}px;
      right: ${$placement.x}px;
      `
        : `
      border-right: solid ${$ratio * 0.15}px ${white};
      border-top: solid ${$ratio * 0.15}px ${red};
      border-top-right-radius: ${$ratio * (24 / 80)}px;
      top: ${$placement.y}px;
      right: ${$placement.x}px;
    `;
  }}
  height: ${({ $placement }) => `${$placement.height}px`};
  width: ${({ $placement }) => `${$placement.width}px`};
  z-index: 15;
`;

const YellowArea = styled.div<{
  $placement: Placement;
  $ratio: number;
  $halfView: boolean;
  $isDrawingEnabled: boolean;
  $reverse: boolean;
}>`
  position: absolute;
  box-sizing: border-box;
  /* box-sizing: ${(props) =>
    props.$halfView ? 'border-box' : 'content-box'}; */
  border-color: ${yellow};
  border-width: ${({ $ratio }) => `${$ratio * 0.15}px`};
  border-style: solid;
  ${({ $halfView, $reverse }) =>
    $halfView
      ? `border-top: none;
         border-bottom: none;`
      : $reverse
        ? `border-left: none;
         border-right: none;`
        : `border-left: none;
         border-right: none;`}
  top: ${({ $placement }) => `${$placement.y as number}px`};
  ${({ $reverse, $placement: { x } }) =>
    $reverse ? `right: ${x}px` : `left: ${x}px`};
  height: ${({ $placement }) => `${$placement.height}px`};
  width: ${({ $placement }) => `${$placement.width}px`};
  z-index: 15;
`;

const GreenArea = styled.div<{
  $placement: Placement;
  $ratio: number;
  $halfView: boolean;
  $isDrawingEnabled: boolean;
  $reverse: boolean;
}>`
  position: absolute;
  box-sizing: border-box;
  border-color: ${green};
  border-width: ${({ $ratio }) => `${$ratio * 0.15}px`};
  ${({ $halfView, $reverse }) =>
    $halfView
      ? `border-top-style: dashed;
         border-left-style: solid;
         border-right-style: solid;
         border-bottom-style: solid;
         border-bottom-color: white;`
      : $reverse
        ? `border-top-style: solid;
         border-bottom-style: solid;
         border-left: none;
         border-right-style: dashed;`
        : `border-top-style: solid;
         border-bottom-style: solid;
         border-right: none;
         border-left-style: dashed;`}
  ${({ $halfView, $ratio }) =>
    $halfView
      ? `border-bottom-left-radius: ${$ratio * (24 / 80)}px;
         border-bottom-right-radius: ${$ratio * (24 / 80)}px;`
      : `border-radius: none;`}
  bottom: 0;
  ${({ $reverse, $placement: { x } }) =>
    $reverse ? `right: ${x}px` : `left: ${x}px`};
  height: ${({ $placement }) => `${$placement.height}px`};
  width: ${({ $placement }) => `${$placement.width}px`};
  z-index: 15;
`;

const Goal = styled.div<{
  $placement: Placement;
  $ratio: number;
  $reverse: boolean;
  $halfView: boolean;
}>`
  position: absolute;
  box-sizing: border-box;
  border-color: ${white};
  border-style: solid;
  border-width: ${(props) => `${props.$ratio * 0.15}px`};
  ${({ $reverse, $halfView }) =>
    $halfView ? '' : $reverse ? `border-left: none;` : `border-right: none;`};
  top: ${(props) => `${props.$placement.y}`};
  ${({ $reverse, $placement: { x } }) =>
    $reverse ? `right: ${x}` : `left: ${x}`};
  height: ${(props) => `${props.$placement.height}px`};
  width: ${(props) => `${props.$placement.width}px`};
  ${({ $ratio, $halfView, $reverse }) => {
    if ($halfView && !$reverse) {
      return `
        border-top-left-radius: ${$ratio * (24 / 80)}px;
        border-top-right-radius: ${$ratio * (24 / 80)}px;
      `;
    }
    if (!$halfView && $reverse) {
      return `
        border-top-right-radius: ${$ratio * (24 / 80)}px;
        border-bottom-right-radius: ${$ratio * (24 / 80)}px;
      `;
    }
    if (!$halfView && !$reverse) {
      return `
        border-top-left-radius: ${$ratio * (24 / 80)}px;
        border-bottom-left-radius: ${$ratio * (24 / 80)}px;
      `;
    }
  }}
  z-index: 25;
`;

const Line = styled.div<{
  $placement: Placement;
  $reverse: boolean;
  $dashed?: boolean;
  $color?: string;
  $halfView?: boolean;
  $ratio: number;
}>`
  position: absolute;
  /* box-sizing: border-box; */
  ${({ $halfView, $dashed, $reverse, $ratio, $color }) =>
    $halfView
      ? `
        border-top-color: ${$color};
        border-top-style: ${$dashed ? 'dashed' : 'solid'};
        border-top-width: ${LINE_WIDTH * $ratio}px;
        `
      : $reverse
        ? `
        border-left-color: ${$color};
        border-left-style: ${$dashed ? 'dashed' : 'solid'};
        border-left-width: ${LINE_WIDTH * $ratio}px;
        `
        : `
        border-right-color: ${$color};
        border-right-style: ${$dashed ? 'dashed' : 'solid'};
        border-right-width: ${LINE_WIDTH * $ratio}px;
        `}
  top: ${(props) => `${props.$placement.y}px`};
  ${({ $reverse, $placement: { x } }) =>
    $reverse ? `right: ${x}px` : `left: ${x}px`};
  height: ${(props) => `${props.$placement.height}px`};
  width: ${(props) => `${props.$placement.width}px`};
`;

const Line5m = (props: {
  $placement: Placement;
  $reverse: boolean;
  $halfView: boolean;
  $ratio: number;
}) => <Line {...props} $dashed={true} $color={green} />;

const GoalArea = styled.div<{
  $placement: Placement;
  $reverse: boolean;
  $ratio: number;
  $halfView: boolean;
}>`
  z-index: 15;
  position: absolute;
  border-color: ${red};
  border-style: dashed;
  box-sizing: border-box;
  border-width: ${({ $ratio }) => `${LINE_WIDTH * $ratio}px`};
  top: ${(props) => `${props.$placement.y}`};
  ${({ $reverse, $placement: { x } }) =>
    $reverse ? `right: ${x}` : `left: ${x}`};
  height: ${(props) => `${props.$placement.height}px`};
  width: ${(props) => `${props.$placement.width}px`};
  border-radius: ${(props) => `${props.$ratio * (24 / 80)}px`};
`;

const pulse = keyframes`
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.3);
    }
`;

const pulseAnimation = css`
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const Player = styled.div<{
  $placement: Placement;
  $halfView: boolean;
  $origin: number;
  $selected?: boolean;
  $number?: string;
}>`
  height: ${(props) => `${props.$placement.height}px`};
  width: ${(props) => `${props.$placement.width}px`};
  z-index: 50;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '${(props) => props.$number || ''}';
    color: ${yellow};
    font-size: 2rem;
    position: absolute;
    font-weight: bold;
  }

  ${(props) => props.$selected && pulseAnimation}
`;

const Ball = styled(Player)`
  height: ${(props) => `${(props.$placement.height as number) / 2}px`};
  width: ${(props) => `${(props.$placement.width as number) / 2}px`};
`;

const LogoImage = styled.img`
  max-height: 50%;
  opacity: 0.25;
`;
const BackgroundText = styled.div<{
  $maxHeight: number;
  $centerOffset: number;
}>`
  color: ${white};
  opacity: 0.35;
  font-size: ${(props) => props.$maxHeight * 0.65}px;
  font-weight: bold;
  position: relative;
  height: ${(props) => `${props.$maxHeight - props.$centerOffset}px`};
  top: ${(props) => `${props.$centerOffset}px`};
`;

const BorderDot = styled.div<{
  $placement: Placement;
  $ratio: number;
  $halfView: boolean;
  $isDrawingEnabled?: boolean;
  $reverse: boolean;
}>`
  position: absolute;
  box-sizing: border-box;
  border-color: ${red};
  border-width: ${({ $ratio }) => `${$ratio * LINE_WIDTH}px`};
  border-style: solid;
  ${({ $halfView }) =>
    $halfView
      ? `border-top: none;
         border-bottom: none;`
      : `border-left: none;
         border-right: none;`}
  top: ${({ $placement }) => `${$placement.y as number}px`};
  ${({ $reverse, $placement: { x } }) =>
    $reverse ? `right: ${x}px` : `left: ${x}px`};
  height: ${({ $placement }) => `${$placement.height}px`};
  width: ${({ $placement }) => `${$placement.width}px`};
  z-index: 16;
`;

export const PoolElement: React.FC<{
  isSubscribedUser?: boolean;
  colorMode?: 'light' | 'dark';
  halfView: boolean;
  className?: string;
  poolMaxHeight: number;
  poolMaxWidth: number;
  snapshot: Snapshot;
  isPositionSelectorOpen?: boolean;
  isDrawingEnabled?: boolean;
  drawingMode?: DrawingMode;
  onStopDrawing?: () => void | null;
  onDrawingsCleaned: () => void | null;
  shouldCleanDrawings: boolean;
  onSnapshotUpdate?: (snapshot: Snapshot) => void;
  onNamingPlayer?: (index: number) => void;
  shouldUndoLastDrawing?: boolean;
  onLastDrawingUndone: () => void | null;
  namings?: (string | null)[];
  opponentIncrease: number;
  isStatic?: boolean;
  playName?: string;
  poolRef?: React.RefObject<HTMLElement> | null;
  onStartNamingPlayer?: (index: number) => void;
  selectedPlayer?: number | null;
  namingEditorActive?: boolean;
  backgroundText?: string;
  movementMode?: MovementMode;
  showSavedPaths?: boolean;
}> = ({
  colorMode = 'light',
  halfView,
  className,
  snapshot,
  poolMaxHeight,
  poolMaxWidth,
  isPositionSelectorOpen = true,
  isDrawingEnabled = false,
  drawingMode = DrawingMode.NONE,
  onStopDrawing = null,
  onDrawingsCleaned,
  shouldCleanDrawings,
  onSnapshotUpdate = () => {},
  onNamingPlayer,
  shouldUndoLastDrawing = false,
  onLastDrawingUndone,
  namings,
  opponentIncrease,
  isStatic = false,
  isSubscribedUser = false,
  playName = '',
  poolRef = null,
  selectedPlayer = null,
  onStartNamingPlayer = (index: number) => () => {},
  namingEditorActive = false,
  backgroundText,
  movementMode = MovementMode.LINEAR,
  showSavedPaths = false,
}) => {
  const [poolHeight, setPoolHeight] = useState<number>(POOL_HEIGHT);
  const [poolWidth, setPoolWidth] = useState<number>(POOL_WIDTH);
  const [positionAdjustment, setPositionAdjustment] = useState<number>(1);
  const [ratio, setRatio] = useState<number>(1);
  const [playerClicked, setPlayerClicked] = useState<number | null>(null);
  const doubleClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [actualHalfView, setActualHalfView] = useState<boolean>(halfView);
  const [currentPlayName, setCurrentPlayName] = useState<string | null>(null);
  const userStore = useStore($auth);

  const logos = ['ftc', 'cnab', 'kesi', 'psn'];

  const userLogo = useMemo(() => {
    if (userStore && userStore.user && userStore.user.userAttributes) {
      // console.log('userStore.user.userAttributes::pool', userStore.user.userAttributes)
      if (
        userStore.user.userAttributes['custom:logo'] &&
        logos.includes(userStore.user.userAttributes['custom:logo'])
      ) {
        return userStore.user.userAttributes['custom:logo'];
      }
    }
    return null;
  }, [userStore]);

  // const [itemPlacements, setItemPlacements] = useState<Record<
  //   string,
  //   Placement
  // > | null>(null)

  // const updateItemPlacements = () => {
  //   const newItemPlacements = {
  //     playArea: {
  //       x: actualHalfView ? 0 : GOAL_LINE_X * ratio,
  //       y: actualHalfView ? GOAL_LINE_X * ratio : 0,
  //       width:
  //         (actualHalfView ? poolHeight : poolWidth - 2 * GOAL_LINE_X) * ratio,
  //       height:
  //         (actualHalfView ? poolWidth / 2 - GOAL_LINE_X : poolHeight) * ratio,
  //     },
  //     redArea: {
  //       x: actualHalfView ? 0 : GOAL_LINE_X * ratio,
  //       y: actualHalfView ? GOAL_LINE_X * ratio : 0,
  //       width:
  //         (actualHalfView
  //           ? (poolHeight - GOAL_AREA_HEIGHT) / 2
  //           : GOAL_AREA_WIDTH) * ratio,
  //       height:
  //         (actualHalfView
  //           ? GOAL_AREA_WIDTH
  //           : (poolHeight - GOAL_AREA_HEIGHT) / 2) * ratio,
  //     },
  //     yellowArea: {
  //       x: actualHalfView ? 0 : (GOAL_LINE_X + GOAL_AREA_WIDTH) * ratio,
  //       y: actualHalfView ? (GOAL_LINE_X + GOAL_AREA_WIDTH) * ratio : 0,
  //       width: (actualHalfView ? poolHeight : YELLOW_AREA_WIDTH) * ratio,
  //       height:
  //         (actualHalfView ? YELLOW_AREA_WIDTH + LINE_WIDTH : poolHeight) *
  //         ratio,
  //     },
  //     greenArea: {
  //       x: actualHalfView
  //         ? 0
  //         : (GOAL_LINE_X + GOAL_AREA_WIDTH + YELLOW_AREA_WIDTH) * ratio,
  //       y: actualHalfView
  //         ? (GOAL_LINE_X + GOAL_AREA_WIDTH + YELLOW_AREA_WIDTH) * ratio
  //         : 0,
  //       width:
  //         (actualHalfView ? poolHeight : GREEN_AREA_WIDTH + LINE_WIDTH * 3) *
  //         ratio,
  //       height:
  //         (actualHalfView ? GREEN_AREA_WIDTH + LINE_WIDTH * 3 : poolHeight) *
  //         ratio,
  //     },
  //     poolArea: {
  //       x: actualHalfView
  //         ? ((poolMaxWidth as number) - poolHeight * ratio) / 2
  //         : ((poolMaxWidth as number) - poolWidth * ratio) / 2,
  //       y: actualHalfView ? POOL_PADDING_Y * ratio : POOL_PADDING_X * ratio,
  //       width: (actualHalfView ? poolHeight : poolWidth) * ratio,
  //       height: (actualHalfView ? poolWidth / 2 : poolHeight) * ratio,
  //     },
  //     goal: {
  //       x: actualHalfView ? `calc(50% - ${(GOAL_WIDTH / 2) * ratio}px)` : "0px",
  //       y: actualHalfView ? "0px" : `calc(50% - ${(GOAL_WIDTH / 2) * ratio}px)`,
  //       width: actualHalfView ? GOAL_WIDTH * ratio : GOAL_DEPTH * ratio,
  //       height: actualHalfView
  //         ? (GOAL_DEPTH + LINE_WIDTH) * ratio
  //         : GOAL_WIDTH * ratio,
  //     },
  //     goal2: {
  //       x: actualHalfView
  //         ? `calc(50% - ${(GOAL_AREA_HEIGHT / 2) * ratio}px)`
  //         : "0px",
  //       y: actualHalfView ? "0px" : `calc(50% - ${(GOAL_WIDTH / 2) * ratio}px)`,
  //       width: actualHalfView ? GOAL_AREA_HEIGHT * ratio : GOAL_DEPTH * ratio,
  //       height: actualHalfView
  //         ? (GOAL_DEPTH + LINE_WIDTH) * ratio
  //         : GOAL_WIDTH * ratio,
  //     },
  //     borderDot: {
  //       x: actualHalfView ? 0 : (GOAL_LINE_X + 5 - LINE_WIDTH * 1) * ratio,
  //       y: actualHalfView ? (GOAL_LINE_X + 5 - LINE_WIDTH * 1) * ratio : 0,
  //       width: (actualHalfView ? poolHeight : LINE_WIDTH * 3) * ratio,
  //       height: (actualHalfView ? LINE_WIDTH * 3 : poolHeight) * ratio,
  //     },
  //     goalLine: {
  //       x: actualHalfView ? 0 : GOAL_LINE_X * ratio,
  //       y: actualHalfView ? GOAL_LINE_X * ratio : 0,
  //       width: actualHalfView ? poolHeight * ratio : LINE_WIDTH * ratio,
  //       height: actualHalfView ? LINE_WIDTH * ratio : poolHeight * ratio,
  //     },
  //     line5m: {
  //       x: actualHalfView ? 0 : LINE_5M_X * ratio,
  //       y: actualHalfView ? LINE_5M_X * ratio : 0,
  //       width: actualHalfView ? poolHeight * ratio : LINE_WIDTH * ratio,
  //       height: actualHalfView ? LINE_WIDTH * ratio : poolHeight * ratio,
  //     },
  //     line2m: {
  //       x: actualHalfView ? 0 : LINE_2M_X * ratio,
  //       y: actualHalfView ? LINE_2M_X * ratio : 0,
  //       width: actualHalfView ? poolHeight * ratio : LINE_WIDTH * ratio,
  //       height: actualHalfView ? LINE_WIDTH * ratio : poolHeight * ratio,
  //     },
  //     goalArea: {
  //       x: actualHalfView
  //         ? `calc(50% - ${(GOAL_AREA_HEIGHT / 2) * ratio}px)`
  //         : `${GOAL_LINE_X * ratio}px`,
  //       y: actualHalfView
  //         ? `${GOAL_LINE_X * ratio}px`
  //         : `calc(50% - ${(GOAL_AREA_HEIGHT / 2) * ratio}px)`,
  //       width: actualHalfView
  //         ? GOAL_AREA_HEIGHT * ratio
  //         : (GOAL_AREA_WIDTH + LINE_WIDTH) * ratio,
  //       height: actualHalfView
  //         ? GOAL_AREA_WIDTH * ratio
  //         : GOAL_AREA_HEIGHT * ratio,
  //     },
  //     player: {
  //       x: 0,
  //       y: 0,
  //       width: PLAYER_SIZE * ratio,
  //       height: PLAYER_SIZE * ratio,
  //     },
  //   }
  //   setItemPlacements(newItemPlacements)
  // }

  const itemPlacements = useMemo(() => {
    if (!poolHeight || !poolWidth || !ratio) {
      return null;
    }
    const newItemPlacements = {
      playArea: {
        x: actualHalfView ? 0 : GOAL_LINE_X * ratio,
        y: actualHalfView ? GOAL_LINE_X * ratio : 0,
        width:
          (actualHalfView ? poolHeight : poolWidth - 2 * GOAL_LINE_X) * ratio,
        height:
          (actualHalfView ? poolWidth / 2 - GOAL_LINE_X : poolHeight) * ratio,
      },
      redArea: {
        x: actualHalfView ? 0 : GOAL_LINE_X * ratio,
        y: actualHalfView ? GOAL_LINE_X * ratio : 0,
        width:
          (actualHalfView
            ? (poolHeight - GOAL_AREA_HEIGHT) / 2
            : GOAL_AREA_WIDTH) * ratio,
        height:
          (actualHalfView
            ? GOAL_AREA_WIDTH
            : (poolHeight - GOAL_AREA_HEIGHT) / 2) * ratio,
      },
      yellowArea: {
        x: actualHalfView ? 0 : (GOAL_LINE_X + GOAL_AREA_WIDTH) * ratio,
        y: actualHalfView ? (GOAL_LINE_X + GOAL_AREA_WIDTH) * ratio : 0,
        width: (actualHalfView ? poolHeight : YELLOW_AREA_WIDTH) * ratio,
        height:
          (actualHalfView ? YELLOW_AREA_WIDTH + LINE_WIDTH : poolHeight) *
          ratio,
      },
      greenArea: {
        x: actualHalfView
          ? 0
          : (GOAL_LINE_X + GOAL_AREA_WIDTH + YELLOW_AREA_WIDTH) * ratio,
        y: actualHalfView
          ? (GOAL_LINE_X + GOAL_AREA_WIDTH + YELLOW_AREA_WIDTH) * ratio
          : 0,
        width:
          (actualHalfView ? poolHeight : GREEN_AREA_WIDTH + LINE_WIDTH * 3) *
          ratio,
        height:
          (actualHalfView ? GREEN_AREA_WIDTH + LINE_WIDTH * 3 : poolHeight) *
          ratio,
      },
      poolArea: {
        x: actualHalfView
          ? ((poolMaxWidth as number) - poolHeight * ratio) / 2
          : ((poolMaxWidth as number) - poolWidth * ratio) / 2,
        y: actualHalfView ? POOL_PADDING_Y * ratio : POOL_PADDING_X * ratio,
        width: (actualHalfView ? poolHeight : poolWidth) * ratio,
        height: (actualHalfView ? poolWidth / 2 : poolHeight) * ratio,
      },
      goal: {
        x: actualHalfView ? `calc(50% - ${(GOAL_WIDTH / 2) * ratio}px)` : '0px',
        y: actualHalfView ? '0px' : `calc(50% - ${(GOAL_WIDTH / 2) * ratio}px)`,
        width: actualHalfView ? GOAL_WIDTH * ratio : GOAL_DEPTH * ratio,
        height: actualHalfView
          ? (GOAL_DEPTH + LINE_WIDTH) * ratio
          : GOAL_WIDTH * ratio,
      },
      goal2: {
        x: actualHalfView
          ? `calc(50% - ${(GOAL_AREA_HEIGHT / 2) * ratio}px)`
          : '0px',
        y: actualHalfView ? '0px' : `calc(50% - ${(GOAL_WIDTH / 2) * ratio}px)`,
        width: actualHalfView ? GOAL_AREA_HEIGHT * ratio : GOAL_DEPTH * ratio,
        height: actualHalfView
          ? (GOAL_DEPTH + LINE_WIDTH) * ratio
          : GOAL_WIDTH * ratio,
      },
      borderDot: {
        x: actualHalfView ? 0 : (GOAL_LINE_X + 5 - LINE_WIDTH * 1) * ratio,
        y: actualHalfView ? (GOAL_LINE_X + 5 - LINE_WIDTH * 1) * ratio : 0,
        width: (actualHalfView ? poolHeight : LINE_WIDTH * 3) * ratio,
        height: (actualHalfView ? LINE_WIDTH * 3 : poolHeight) * ratio,
      },
      goalLine: {
        x: actualHalfView ? 0 : GOAL_LINE_X * ratio,
        y: actualHalfView ? GOAL_LINE_X * ratio : 0,
        width: actualHalfView ? poolHeight * ratio : LINE_WIDTH * ratio,
        height: actualHalfView ? LINE_WIDTH * ratio : poolHeight * ratio,
      },
      line5m: {
        x: actualHalfView ? 0 : LINE_5M_X * ratio,
        y: actualHalfView ? LINE_5M_X * ratio : 0,
        width: actualHalfView ? poolHeight * ratio : LINE_WIDTH * ratio,
        height: actualHalfView ? LINE_WIDTH * ratio : poolHeight * ratio,
      },
      line2m: {
        x: actualHalfView ? 0 : LINE_2M_X * ratio,
        y: actualHalfView ? LINE_2M_X * ratio : 0,
        width: actualHalfView ? poolHeight * ratio : LINE_WIDTH * ratio,
        height: actualHalfView ? LINE_WIDTH * ratio : poolHeight * ratio,
      },
      goalArea: {
        x: actualHalfView
          ? `calc(50% - ${(GOAL_AREA_HEIGHT / 2) * ratio}px)`
          : `${GOAL_LINE_X * ratio}px`,
        y: actualHalfView
          ? `${GOAL_LINE_X * ratio}px`
          : `calc(50% - ${(GOAL_AREA_HEIGHT / 2) * ratio}px)`,
        width: actualHalfView
          ? GOAL_AREA_HEIGHT * ratio
          : (GOAL_AREA_WIDTH + LINE_WIDTH) * ratio,
        height: actualHalfView
          ? GOAL_AREA_WIDTH * ratio
          : GOAL_AREA_HEIGHT * ratio,
      },
      player: {
        x: 0,
        y: 0,
        width: PLAYER_SIZE * ratio,
        height: PLAYER_SIZE * ratio,
      },
    };
    return newItemPlacements;
  }, [actualHalfView, poolHeight, poolWidth, ratio, poolMaxWidth, halfView]);

  useEffect(() => {
    const newRatio = actualHalfView
      ? (poolMaxHeight as number) / (POOL_FULL_WIDTH / 2 + 2 * POOL_PADDING_Y)
      : Math.min(
          (poolMaxHeight as number) / POOL_FULL_HEIGHT,
          (poolMaxWidth as number) / POOL_FULL_WIDTH,
        );

    setRatio(newRatio);

    const newPoolHeight = actualHalfView
      ? Math.min(
          Math.floor((poolMaxWidth as number) / newRatio) - 2 * POOL_PADDING_X,
          25,
        )
      : POOL_HEIGHT;
    setPoolHeight(isPositionSelectorOpen ? POOL_HEIGHT : newPoolHeight);

    const newPoolWidth = actualHalfView
      ? POOL_WIDTH
      : // This 5 is here just to get some more padding
        Math.floor((poolMaxWidth as number) / newRatio) - 5 * POOL_PADDING_Y;

    setPoolWidth(newPoolWidth);

    const newPositionAdjustment = actualHalfView
      ? newPoolHeight / POOL_HEIGHT
      : newPoolWidth / POOL_WIDTH;
    setPositionAdjustment(isPositionSelectorOpen ? 1 : newPositionAdjustment);
  }, [poolMaxHeight, actualHalfView, poolMaxWidth, isPositionSelectorOpen]);

  // useEffect(() => {
  //   updateItemPlacements()
  // }, [ratio, poolHeight, actualHalfView, snapshot, poolWidth, positionAdjustment])

  // ----- Path recording (movement mode PATH)
  // The trail is buffered in a ref: the position-derived Draggable keys
  // remount every piece right after onStop commits the snapshot, so any
  // in-component state would be lost; and writing to the store per sample
  // would flood the Cache API via $tactic.subscribe. The buffer is keyed by
  // piece so simultaneous multi-touch drags cannot interleave samples — the
  // last drag to start owns the buffer, the other piece falls back to
  // linear-drag semantics.
  const dragTrailRef = useRef<{ pieceKey: string; points: Position[] } | null>(
    null,
  );
  const liveTrailUpdatedAtRef = useRef<number>(0);
  const [liveTrail, setLiveTrail] = useState<LiveTrail | null>(null);

  const getPiecePosition = (kind: PieceKind, index: number): Position =>
    kind === 'ball' ? snapshot[kind] : snapshot[kind][index];

  // Inverse of the piece placement math: accumulated drag offset (px) back to
  // pool metres. Same formulas handleDragStop always used; extracted so the
  // per-move trail sampling shares them.
  const dragOffsetToPoolPosition = (
    base: Position,
    offsetX: number,
    offsetY: number,
  ): Position =>
    actualHalfView
      ? {
          x:
            (base.x * ratio * positionAdjustment + offsetX) /
            ratio /
            positionAdjustment,
          y: (base.y * ratio + offsetY) / ratio,
        }
      : {
          x: (base.x * ratio + offsetY) / ratio,
          y: (base.y * ratio - offsetX) / ratio,
        };

  const isRecordingPaths = movementMode === MovementMode.PATH && !isStatic;

  const handleDragStart = (kind: PieceKind, index: number) => () => {
    // Clear any trail orphaned by a cancelled drag (touchcancel, mid-drag
    // remount) — a no-op re-render when there is nothing to clear.
    setLiveTrail(null);
    if (!isRecordingPaths) {
      dragTrailRef.current = null;
      return;
    }
    const base = getPiecePosition(kind, index);
    dragTrailRef.current = {
      pieceKey: `${kind}-${index}`,
      points: [{ x: base.x, y: base.y }],
    };
    liveTrailUpdatedAtRef.current = 0;
  };

  const handleDrag = (kind: PieceKind, index: number) => (event, ui) => {
    if (!isRecordingPaths) {
      return;
    }
    const trail = dragTrailRef.current;
    if (!trail || trail.pieceKey !== `${kind}-${index}`) {
      return;
    }
    const base = getPiecePosition(kind, index);
    const position = dragOffsetToPoolPosition(base, ui.x, ui.y);
    const last = trail.points[trail.points.length - 1];
    if (last) {
      const dx = position.x - last.x;
      const dy = position.y - last.y;
      if (dx * dx + dy * dy < MIN_SAMPLE_DISTANCE * MIN_SAMPLE_DISTANCE) {
        return;
      }
    }
    trail.points.push(position);
    // Throttle the visible trail: pushing state per pointer move would
    // re-render the whole pool at pointer rate (iPad main thread).
    const now = Date.now();
    if (now - liveTrailUpdatedAtRef.current >= 80) {
      liveTrailUpdatedAtRef.current = now;
      setLiveTrail({ kind, points: [...trail.points] });
    }
  };

  const handleDragStop = (kind: PieceKind, index: number) => (event, ui) => {
    const base = getPiecePosition(kind, index);

    const newPositionlist =
      kind === 'ball' ? [snapshot[kind]] : [...snapshot[kind]];

    const newPosition = dragOffsetToPoolPosition(base, ui.lastX, ui.lastY);

    newPositionlist[kind === 'ball' ? 0 : index] = newPosition;

    const nextSnapshot: Snapshot = {
      ...snapshot,
      [kind]: kind === 'ball' ? newPositionlist[0] : newPositionlist,
    };

    if (!isStatic) {
      const buffered = dragTrailRef.current;
      const ownsTrail = !!buffered && buffered.pieceKey === `${kind}-${index}`;
      if (ownsTrail) {
        dragTrailRef.current = null;
      }
      setLiveTrail(null);
      // A tap fires onStop with a zero delta; it must not rewrite or clear
      // recorded paths — only an actual drag does.
      const moved = Math.abs(ui.lastX) > 2 || Math.abs(ui.lastY) > 2;
      if (moved) {
        nextSnapshot.paths = applyDragToPathSet(
          snapshot.paths,
          kind,
          index,
          ownsTrail ? buffered!.points : [],
          newPosition,
          isRecordingPaths,
        );
      }
    }

    onSnapshotUpdate(nextSnapshot);
  };

  // Pool-metre position -> screen px of the PIECE CENTRE, mirroring the
  // piece divs' placement (full view anchors from the right edge).
  const poolPositionToScreen = (
    position: Position,
    kind: PieceKind,
  ): { x: number; y: number } => {
    const half = (PLAYER_SIZE * ratio * (kind === 'ball' ? 0.5 : 1)) / 2;
    if (actualHalfView) {
      return {
        x: position.x * positionAdjustment * ratio + half,
        y: position.y * ratio + half,
      };
    }
    const poolScreenWidth = (itemPlacements?.poolArea.width as number) || 0;
    return {
      x: poolScreenWidth - position.y * ratio - half,
      y: position.x * ratio + half,
    };
  };

  const onPlayerClick = (index) => {
    if (namingEditorActive && onNamingPlayer) {
      return () => !!onNamingPlayer && onNamingPlayer(index);
    }
    return onDoubleClick(index);
  };

  const onDoubleClick = (index) => () => {
    return;
    if (playerClicked === index) {
      clearTimeout(doubleClickTimer.current!);
      setPlayerClicked(null);
      onStartNamingPlayer && onStartNamingPlayer(index);
    }
    setPlayerClicked(index);
    clearTimeout(doubleClickTimer.current!);
    doubleClickTimer.current = setTimeout(() => {
      setPlayerClicked(null);
    }, 400);
  };

  const logoImageComponent = useMemo(() => {
    console.log('userLogo', userLogo);
    if (userLogo && !isStatic && !backgroundText) {
      let mixBlendMode: CSSProperties['mixBlendMode'] = 'normal';
      let logo = '';
      let opacity = 0.25;
      switch (userLogo) {
        case 'kesi':
          logo = kesiLogo;
          mixBlendMode = 'overlay';
          break;
        case 'psn':
          logo = psnLogo;
          mixBlendMode = 'overlay';
          break;
        case 'cnab':
          logo = cnabLogo;
          mixBlendMode = 'overlay';
          break;
        case 'ftc':
          logo = ftcLogo;
          break;
        default:
          logo = '';
          break;
      }
      return (
        <LogoImage
          style={{
            userSelect: 'none',
            pointerEvents: 'none',
            mixBlendMode: mixBlendMode,
            opacity: opacity,
          }}
          src={logo}
          alt="Logo"
        />
      );
    }
    return <></>;
  }, [userLogo, isStatic, backgroundText]);

  // If there's any elemnent (player, opp, ball) in the snapshot
  // outside the half pool, we rotate the screen to full view
  useEffect(() => {
    // if we're already in full view, we don't need to do anything
    setActualHalfView(halfView);
    if (!halfView) {
      return;
    }
    // if we're not in the sidebar we trust the user to set the correct view
    if (!isStatic) {
      return;
    }
    const isElementOutsideHalfPool = (position: Position) => {
      return (position.y as number) >= POOL_WIDTH / 2;
    };
    const elements = [
      ...snapshot.players,
      ...snapshot.opponents,
      snapshot.ball,
    ];
    const isAnyElementOutsideHalfPool = elements.some(isElementOutsideHalfPool);
    setActualHalfView(!isAnyElementOutsideHalfPool);
  }, [snapshot, halfView, isStatic]);

  useEffect(() => {
    if (playName !== currentPlayName) {
      setCurrentPlayName(playName);
    }
  }, [playName, currentPlayName]);

  if (!itemPlacements) {
    return null;
  }
  return (
    <Pool
      $placement={itemPlacements.poolArea}
      $ratio={ratio}
      $halfView={actualHalfView}
      $isDrawingEnabled={isDrawingEnabled}
      key={`pool-${currentPlayName || ''}-2`}
      ref={poolRef as unknown as React.RefObject<HTMLDivElement>}
    >
      {isDrawingEnabled && !isStatic && (
        <PositionedDrawingCanvas
          $placement={itemPlacements.playArea}
          $isActive={drawingMode !== DrawingMode.NONE}
          $ratio={ratio}
          width={itemPlacements.playArea.width as number}
          height={itemPlacements.playArea.height as number}
          mode={drawingMode}
          lineWidth={LINE_WIDTH * ratio * 1.5}
          shouldCleanDrawings={shouldCleanDrawings}
          onDrawingsCleaned={onDrawingsCleaned}
          shouldUndoLastDrawing={shouldUndoLastDrawing}
          onLastDrawingUndone={onLastDrawingUndone}
        />
      )}
      <PlayArea
        $placement={itemPlacements.playArea}
        $ratio={ratio}
        $halfView={actualHalfView}
        $isDrawingEnabled={isDrawingEnabled}
        key={`pool-${currentPlayName || ''}`}
      />
      {!isStatic && (liveTrail || (showSavedPaths && snapshot.paths)) && (
        <PathOverlay
          width={itemPlacements.poolArea.width as number}
          height={itemPlacements.poolArea.height as number}
          strokeWidth={LINE_WIDTH * ratio * 1.5}
          toScreen={poolPositionToScreen}
          liveTrail={liveTrail}
          savedPaths={showSavedPaths ? snapshot.paths : null}
        />
      )}
      {logoImageComponent}
      {!!backgroundText && (
        <BackgroundText
          $maxHeight={poolMaxHeight}
          $centerOffset={
            actualHalfView ? (itemPlacements.goal.height as number) : 0
          }
        >
          {backgroundText}
        </BackgroundText>
      )}
      {snapshot['players'].map((value, index) => {
        const capNumber = namings?.[index] || '';
        const isPlayerSelected = selectedPlayer === index;
        return (
          <Draggable
            disabled={isStatic || namingEditorActive}
            onStart={handleDragStart('players', index)}
            onDrag={handleDrag('players', index)}
            onStop={handleDragStop('players', index)}
            key={`players-${value.x}-${value.y}`}
          >
            <Player
              onClick={onPlayerClick(index)}
              onTouchStart={onPlayerClick(index)}
              $placement={itemPlacements.player}
              $halfView={actualHalfView}
              $origin={POOL_HEIGHT * ratio}
              $selected={isPlayerSelected}
              style={
                actualHalfView
                  ? {
                      top: (value.y as number) * ratio,
                      left: (value.x as number) * positionAdjustment * ratio,
                    }
                  : {
                      right: (value.y as number) * ratio,
                      top: (value.x as number) * ratio,
                    }
              }
              $number={capNumber}
            >
              <PlayerOwnDisk
                style={{ width: '100%' }}
                key={`player-own-${index}`}
              />
            </Player>
          </Draggable>
        );
      })}
      {snapshot['opponents'].map((value, index) => {
        const opponentIndex = index + opponentIncrease;
        const capNumber = namings?.[opponentIndex] || '';
        const isPlayerSelected = selectedPlayer === opponentIndex;
        return (
          <Draggable
            disabled={isStatic || namingEditorActive}
            onStart={handleDragStart('opponents', index)}
            onDrag={handleDrag('opponents', index)}
            onStop={handleDragStop('opponents', index)}
            key={`opponents-${value.x}-${value.y}`}
          >
            <Player
              className={'plr'}
              onClick={onPlayerClick(opponentIndex)}
              onTouchStart={onPlayerClick(opponentIndex)}
              $placement={itemPlacements.player}
              $halfView={actualHalfView}
              $origin={POOL_HEIGHT * ratio}
              $selected={isPlayerSelected}
              $number={capNumber}
              style={
                actualHalfView
                  ? {
                      top: (value.y as number) * ratio,
                      left: (value.x as number) * positionAdjustment * ratio,
                    }
                  : {
                      right: (value.y as number) * ratio,
                      top: (value.x as number) * ratio,
                    }
              }
            >
              <PlayerOpponentDisk
                style={{ width: '100%' }}
                className={'player-opponent'}
                key={`player-opponent-${index}`}
              />
            </Player>
          </Draggable>
        );
      })}
      <Draggable
        disabled={isStatic || namingEditorActive}
        onStart={handleDragStart('goalkeepers', 0)}
        onDrag={handleDrag('goalkeepers', 0)}
        onStop={handleDragStop('goalkeepers', 0)}
        key={`goalkeepers-${snapshot['goalkeepers'][0].x}-${snapshot['goalkeepers'][0].y}`}
      >
        <Player
          $placement={itemPlacements.player}
          $halfView={!!actualHalfView}
          $origin={POOL_HEIGHT * ratio}
          style={
            actualHalfView
              ? {
                  top: (snapshot['goalkeepers'][0].y as number) * ratio,
                  left:
                    (snapshot['goalkeepers'][0].x as number) *
                    positionAdjustment *
                    ratio,
                }
              : {
                  right: (snapshot['goalkeepers'][0].y as number) * ratio,
                  top: (snapshot['goalkeepers'][0].x as number) * ratio,
                }
          }
        >
          <GoalKeeperDisk style={{ width: '100%' }} />
        </Player>
      </Draggable>
      <Draggable
        disabled={isStatic || namingEditorActive}
        onStart={handleDragStart('ball', 0)}
        onDrag={handleDrag('ball', 0)}
        onStop={handleDragStop('ball', 0)}
        key={`ball-${snapshot['ball'].x}-${snapshot['ball'].y}`}
      >
        <Ball
          $placement={itemPlacements.player}
          $halfView={actualHalfView}
          $origin={POOL_HEIGHT * ratio}
          style={
            actualHalfView
              ? {
                  top: (snapshot['ball'].y as number) * ratio,
                  left:
                    (snapshot['ball'].x as number) * positionAdjustment * ratio,
                }
              : {
                  right: (snapshot['ball'].y as number) * ratio,
                  top: (snapshot['ball'].x as number) * ratio,
                }
          }
        >
          <BallDisk style={{ width: '100%' }} />
        </Ball>
      </Draggable>
      <Goal
        $placement={itemPlacements.goal}
        $ratio={ratio}
        $reverse={false}
        $halfView={actualHalfView}
      />
      <GoalArea
        $placement={itemPlacements.goalArea}
        $reverse={false}
        $ratio={ratio}
        $halfView={actualHalfView}
      />
      <RedAreaLeftSide
        $placement={itemPlacements.redArea}
        $ratio={ratio}
        $halfView={actualHalfView}
        $isDrawingEnabled={isDrawingEnabled}
        $reverse={false}
      />
      <RedAreaRightSide
        $placement={itemPlacements.redArea}
        $ratio={ratio}
        $halfView={actualHalfView}
        $isDrawingEnabled={isDrawingEnabled}
        $reverse={false}
      />
      <YellowArea
        $placement={itemPlacements.yellowArea}
        $ratio={ratio}
        $halfView={actualHalfView}
        $isDrawingEnabled={isDrawingEnabled}
        $reverse={false}
      />
      <GreenArea
        $placement={itemPlacements.greenArea}
        $ratio={ratio}
        $halfView={actualHalfView}
        $isDrawingEnabled={isDrawingEnabled}
        $reverse={false}
      />
      <BorderDot
        $placement={itemPlacements.borderDot}
        $ratio={ratio}
        $halfView={actualHalfView}
        $reverse={false}
      />
      {!actualHalfView && (
        <>
          <GoalArea
            $placement={itemPlacements.goalArea}
            $reverse
            $ratio={ratio}
            $halfView={actualHalfView}
          />
          <Goal
            $placement={itemPlacements.goal}
            $ratio={ratio}
            $reverse
            $halfView={actualHalfView}
          />
          <YellowArea
            $placement={itemPlacements.yellowArea}
            $ratio={ratio}
            $halfView={actualHalfView}
            $isDrawingEnabled={isDrawingEnabled}
            $reverse
          />
          <GreenArea
            $placement={itemPlacements.greenArea}
            $ratio={ratio}
            $halfView={actualHalfView}
            $isDrawingEnabled={isDrawingEnabled}
            $reverse
          />
          <RedAreaLeftSide
            $placement={itemPlacements.redArea}
            $ratio={ratio}
            $halfView={actualHalfView}
            $isDrawingEnabled={isDrawingEnabled}
            $reverse
          />
          <RedAreaRightSide
            $placement={itemPlacements.redArea}
            $ratio={ratio}
            $halfView={actualHalfView}
            $isDrawingEnabled={isDrawingEnabled}
            $reverse
          />
          <BorderDot
            $placement={itemPlacements.borderDot}
            $ratio={ratio}
            $halfView={actualHalfView}
            $reverse
          />
        </>
      )}
    </Pool>
  );
};
